import { OrderPlanningRowAction, OrderPlanningRowActionProps, ReasonMap } from "./OrderPlanningRowAction";
import { Button } from "@mcleod/components";
import { ModelRow, StringUtil } from "@mcleod/core";
import { getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { Dispatch } from "../../asset/Dispatch";
import { ModelEquipmentGroup } from "../../models/ModelEquipmentGroup";
import { EquipmentGroupValidation } from "../../asset/EquipmentGroupValidation";

export class RowActionDispatch extends OrderPlanningRowAction {
    override getButton(): Button {
        return this.toolPanel.buttonDispatch;
    }

    override getButtonId(): string {
        return "buttonDispatch";
    }

    override getAdditionalProps() {
        return this.getFindNearAfterSlideoutProp();
    }

    override getFieldsUsedInThisFile(): string[] {
        return ["tractor", "driver1_id", "driver2_id", "move_status", "order_id", "hold", "manifest_hold",
            "revenue_code", "brokerage", "np_order_id", "np_equip_group_id", "equip_group_id"];
    }

    override async actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap): Promise<void> {
        const modelRow = selectedRows[0];
        if (modelRow.getBoolean("brokerage", false)) {
            this.addReason(reasonMap, "brokered_move", "Cannot dispatch a brokered move.");
        }
        if (modelRow.isNull("tractor") || modelRow.isNull("driver1_id")) {
            this.addReason(reasonMap, "missing_tractor_or_driver", "Cannot dispatch without a tractor or driver.");
        }
        if ((LMEGeneralSettings.getSingleton().getDispatchControl().prevent_dsp_hold === "Y") && (modelRow.getBoolean("hold", false))) {
            this.addReason(reasonMap, "hold", "Cannot dispatch an order that's on hold.");
        }
        if (modelRow.getBoolean("manifest_hold", false)) {
            this.addReason(reasonMap, "manifest_hold", "Cannot Dispatch to a manifest on hold.");
        }
        if (getDispatchControlBoolean("enforce_revenu_cod") && modelRow.isNull("revenue_code")) {
            this.addReason(reasonMap, "revenue_code", "Revenue codes are enforced and required to dispatch.");
        }
        const equipAndDelivered = "tableEquipMoves" === this.toolPanel.table.id && "D" === modelRow.get("move_status", "");
        const equipGroupId = modelRow.get(equipAndDelivered ? "np_equip_group_id" : "equip_group_id");
        if (equipGroupId != null) {
            const equipmentGroupRow = await new ModelEquipmentGroup().searchSingle({ id: equipGroupId });
            const validationResult = await EquipmentGroupValidation.validate({
                modelRow: equipmentGroupRow,
                driverId: modelRow.get("driver1_id", null),
                driver2Id: modelRow.get("driver2_id", null),
                tractorId: modelRow.get("tractor", null),
                field: EquipmentGroupValidation.getDispatchByField()
            });
            const stopDispatchWarnings = validationResult.equipmentWarnings.filter(warning => warning.equipment_requirement == "A" || warning.equipment_requirement == "N");
            let reason: string;
            if (stopDispatchWarnings.length > 0) {
                reason = "Equipment does not meet requirements. Issue required equipment or reassign this load to another Driver to enable Dispatch.";
            }
            if (validationResult.driverEvents) {
                validationResult.driverEvents = validationResult.driverEvents.filter(event => EquipmentGroupValidation.driverEventDispatchFilter(event));
                const shipDate = modelRow.getDate(equipAndDelivered ? "np_origin_early" : "origin_early");
                if (validationResult.driverEvents?.find(row =>
                    !row.getBoolean("equipstatuscode.allow_dispatch", true)
                    && shipDate >= row.getDate("equipstatus.start_date")
                    && shipDate <= row.getDate("equipstatus.end_date")
                )) {
                    reason = "There is a conflicting Driver Event that does not allow dispatching. Reassign this load to another Driver to enable Dispatch.";
                }
                if (validationResult.errors.length > 0) {
                    reason = validationResult.errors.reduce((res, error) => res += error += "\n", "");
                }
            }
            if (reason != null) {
                this.addReason(reasonMap, "equip_group_validation", reason);
            }
        }
    }

    override execute(props: OrderPlanningRowActionProps) {
        const modelRow = props.selectedRows[0]?.data as ModelRow;
        if (modelRow == null) {
            return;
        }

        this.getButton().busy = true;
        const datasourceId = this.toolPanel.table?.dataSource?.id;
        let orderId: string;
        if ("sourceAvailableMove" === datasourceId) {
            orderId = modelRow.get("order_id");
        } else {
            const orderIdFromTractor = modelRow.get("order_id");
            const nextPreassignmentOrderId = modelRow.get("np_order_id");
            orderId = StringUtil.isEmptyString(orderIdFromTractor) ? nextPreassignmentOrderId : orderIdFromTractor;
        }

        const equipmentId = getDispatchControlValue("dispatch_by") == "T" ? modelRow.get("tractor") : modelRow.get("driver1_id");
        Dispatch.openSlideout({
            equipmentId,
            equipmentType: getDispatchControlValue("dispatch_by"),
            orderId: orderId,
            slideoutTitle: "Dispatch",
            doAfterSlideout: (hasChanged:boolean) => {
                if (props.afterSlideout)
                    props.afterSlideout(null)
            }
        });
        this.getButton().busy = false;
    }
}
